import type { ReactNode } from "react";

import { getContractPaymentTerms } from "@/lib/opportunity-utils";

/* Shared listing body for the public opportunity surfaces. See
   `OpportunityListingSections` at the bottom of the file. */

export type ListingSectionsScale = "detail" | "invite";

type HeadingElement = "div" | "h2" | "h3";

interface ScaleTokens {
  block: string;
  heading: string;
  list: string;
  item: string;
  paragraph: string;
  markdownH1: string;
  markdownH2: string;
  markdownH3: string;
  markdownList: string;
  // Divs on detail only: the marketing theme restyles every h2/h3 it wraps.
  sectionAs: HeadingElement;
  subsectionAs: HeadingElement;
}

const SCALE_TOKENS: Record<ListingSectionsScale, ScaleTokens> = {
  detail: {
    block: "mb-6",
    heading: "text-foreground mb-1.5 text-[13.5px] font-semibold",
    list: "list-none space-y-0.5 p-0",
    item: "text-muted-foreground relative py-0.5 pr-0 pl-5 text-[14px] leading-[1.65] font-light before:absolute before:top-[13px] before:left-0 before:h-[4px] before:w-[4px] before:rounded-full before:bg-current before:opacity-25 before:content-['']",
    paragraph:
      "text-muted-foreground mb-2 text-[14px] leading-[1.7] font-light",
    markdownH1: "text-foreground mt-5 mb-1.5 text-[14px] font-semibold",
    markdownH2: "text-foreground mt-5 mb-1.5 text-[13.5px] font-semibold",
    markdownH3: "text-foreground mt-4 mb-1.5 text-[13px] font-medium",
    markdownList: "my-1.5 list-none space-y-0.5 p-0",
    sectionAs: "div",
    subsectionAs: "div",
  },
  invite: {
    // first:mt-0 so the leading block sits flush under whatever the page put
    // above it, instead of stacking two gaps.
    block: "mt-8 first:mt-0",
    heading:
      "text-foreground mb-2 text-[17px] font-semibold tracking-[-0.01em]",
    list: "flex list-none flex-col gap-2 p-0",
    item: "text-muted-foreground relative pl-5 text-[15px] leading-[1.55] before:absolute before:top-[10px] before:left-0 before:h-[5px] before:w-[5px] before:rounded-full before:bg-current before:opacity-30 before:content-['']",
    paragraph: "text-muted-foreground mb-3 text-[15px] leading-[1.65]",
    markdownH1:
      "text-foreground mt-8 mb-2 text-[18px] font-semibold tracking-[-0.01em] first:mt-0",
    markdownH2:
      "text-foreground mt-8 mb-2 text-[17px] font-semibold tracking-[-0.01em] first:mt-0",
    markdownH3: "text-foreground mt-6 mb-2 text-[15px] font-semibold",
    markdownList: "my-2 flex list-none flex-col gap-2 p-0",
    sectionAs: "h2",
    subsectionAs: "h3",
  },
};

const KEY_SEPARATOR = "␟";

// Repeated copy would collide on a text-only key. Occurrence goes in front: a
// suffix isn't injective, ["A", "A (2)", "A"] collides again.
function withStableKeys(items: string[]): { key: string; text: string }[] {
  const seen = new Map<string, number>();
  return items.map((text) => {
    const occurrence = (seen.get(text) ?? 0) + 1;
    seen.set(text, occurrence);
    return { key: `${occurrence}${KEY_SEPARATOR}${text}`, text };
  });
}

function Heading({
  as: As,
  className,
  children,
}: {
  as: HeadingElement;
  className: string;
  children: ReactNode;
}) {
  return <As className={className}>{children}</As>;
}

// Only what the generator emits (`##` + prose) plus bullets for hand-edited
// copy. Not a markdown lib: output has to match the surrounding type scale.
function renderListingMarkdown(text: string, tokens: ScaleTokens): ReactNode[] {
  const elements: ReactNode[] = [];
  let listItems: string[] = [];
  let elementKey = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elementKey++}`} className={tokens.markdownList}>
          {withStableKeys(listItems).map(({ key, text: bullet }) => (
            <li key={key} className={tokens.item}>
              {bullet}
            </li>
          ))}
        </ul>,
      );
      listItems = [];
    }
  };

  text.split("\n").forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }

    if (
      trimmed.startsWith("- ") ||
      trimmed.startsWith("* ") ||
      trimmed.startsWith("• ")
    ) {
      listItems.push(trimmed.slice(2));
      return;
    }

    flushList();

    if (trimmed.startsWith("### ")) {
      elements.push(
        <Heading
          key={`h-${elementKey++}`}
          as={tokens.subsectionAs}
          className={tokens.markdownH3}
        >
          {trimmed.slice(4)}
        </Heading>,
      );
    } else if (trimmed.startsWith("## ")) {
      elements.push(
        <Heading
          key={`h-${elementKey++}`}
          as={tokens.sectionAs}
          className={tokens.markdownH2}
        >
          {trimmed.slice(3)}
        </Heading>,
      );
    } else if (trimmed.startsWith("# ")) {
      elements.push(
        <Heading
          key={`h-${elementKey++}`}
          as={tokens.sectionAs}
          className={tokens.markdownH1}
        >
          {trimmed.slice(2)}
        </Heading>,
      );
    } else {
      elements.push(
        <p key={`p-${elementKey++}`} className={tokens.paragraph}>
          {trimmed}
        </p>,
      );
    }
  });

  flushList();
  return elements;
}

function BulletSection({
  title,
  items,
  tokens,
}: {
  title: string;
  items: string[];
  tokens: ScaleTokens;
}) {
  return (
    <div className={tokens.block}>
      <Heading as={tokens.sectionAs} className={tokens.heading}>
        {title}
      </Heading>
      <ul className={tokens.list}>
        {withStableKeys(items).map(({ key, text }) => (
          <li key={key} className={tokens.item}>
            {text}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface OpportunityListingSectionsProps {
  descriptionMarkdown?: string | null;
  /** Plain prose shown under "About this opportunity" when there's no body. */
  fallbackDescription?: string | null;
  responsibilities?: string[] | null;
  requirements?: string[] | null;
  /** Study pay cadence; picks the payment line in the contract terms. */
  payRateFrequency?: string | null;
  scale: ListingSectionsScale;
}

/**
 * Listing body shared by the recruitment-link invite and the public detail
 * page, so the two can't drift on what a candidate is told before applying.
 * `scale` picks the type scale and heading element, nothing else.
 */
export function OpportunityListingSections({
  descriptionMarkdown,
  fallbackDescription,
  responsibilities,
  requirements,
  payRateFrequency,
  scale,
}: OpportunityListingSectionsProps) {
  const tokens = SCALE_TOKENS[scale];
  // Trimmed, not null-checked: a blank body would suppress the fallback too.
  const body = descriptionMarkdown?.trim() ? descriptionMarkdown : null;
  const fallback =
    !body && fallbackDescription?.trim() ? fallbackDescription : null;
  const hasResponsibilities = Boolean(responsibilities?.length);
  const hasRequirements = Boolean(requirements?.length);

  // Invite only: there the terms would be the page's only section, saying
  // nothing about the work. On detail they sit under the hero and pay card, and
  // were unconditional before this component existed.
  const isEmpty =
    !body && !fallback && !hasResponsibilities && !hasRequirements;
  if (isEmpty && scale === "invite") {
    return null;
  }

  return (
    <>
      {body && (
        <div className={tokens.block}>
          {renderListingMarkdown(body, tokens)}
        </div>
      )}

      {fallback && (
        <div className={tokens.block}>
          <Heading as={tokens.sectionAs} className={tokens.heading}>
            About this opportunity
          </Heading>
          <p className={tokens.paragraph}>{fallback}</p>
        </div>
      )}

      {hasResponsibilities && (
        <BulletSection
          title="What You'll Do"
          items={responsibilities!}
          tokens={tokens}
        />
      )}

      {hasRequirements && (
        <BulletSection
          title="Who Should Apply"
          items={requirements!}
          tokens={tokens}
        />
      )}

      <BulletSection
        title="Contract & Payment Terms"
        items={getContractPaymentTerms(payRateFrequency)}
        tokens={tokens}
      />
    </>
  );
}
